import { registerSW } from 'virtual:pwa-register'
import type { ApplyUpdate } from './pending-update'

/**
 * Enregistrement du Service Worker.
 *
 * C'est lui qui sert l'*app shell* précaché : sans lui, l'app ne s'ouvre pas hors ligne
 * (ARCHITECTURE.md **D19**). Le module virtuel `virtual:pwa-register` est fourni par
 * vite-plugin-pwa ; en développement le Service Worker est désactivé et cette fonction
 * ne fait rien.
 *
 * ⚠️ **Ce module n'est pas testable unitairement, et c'est structurel** : `virtual:pwa-register`
 * n'est résolu que par le build de production — la configuration Vitest ne monte pas le plugin
 * PWA. Il ne porte donc que du branchement, et pas une décision : ce que l'invite affiche et
 * quand elle revient se vérifie sur l'écran (`app/routes/root.spec.tsx`), ce qui est retenu
 * entre l'annonce et l'accord se vérifie dans `pending-update.spec.ts`.
 *
 * @param announcePendingUpdate - Où déposer la version qui attend, pour que l'écran l'offre.
 */
export function registerServiceWorker(announcePendingUpdate: (apply: ApplyUpdate) => void): void {
  const applyUpdate = registerSW({
    // Sans différé côté Workbox : le calendrier est tenu en amont par `runAfterPageLoad()`
    // (cf. `src/main.tsx`), qui n'appelle cette fonction qu'une fois la page chargée.
    // `immediate: false` ferait attendre Workbox une seconde fois, sur un événement `load`
    // déjà passé.
    immediate: true,

    /**
     * Une nouvelle version est précachée et attend. Tant que personne n'appelle
     * `applyUpdate()`, l'agent continue de travailler sur la version en cours —
     * c'est voulu (`registerType: 'prompt'`, cf. `vite.config.ts`).
     *
     * ⚠️ `applyUpdate` est lu **dans** la fermeture, pas à l'appel : Workbox n'invoque cette
     * fonction qu'une fois l'enregistrement résolu, donc bien après l'affectation. Sa valeur de
     * retour est une promesse que personne n'attend — la page part au rechargement.
     */
    onNeedRefresh() {
      console.info('[pwa] Nouvelle version disponible — en attente de validation.')
      announcePendingUpdate(() => {
        void applyUpdate()
      })
    },

    /** Le shell est précaché : l'app est utilisable sans réseau à partir de maintenant. */
    onOfflineReady() {
      console.info('[pwa] Application prête pour une utilisation hors ligne.')
    },

    onRegisterError(error) {
      console.error("[pwa] Échec de l'enregistrement du Service Worker", error)
    },
  })
}
